import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  Inbox,
  MessagesSquare,
  RotateCcw,
  Search,
  Send,
  XCircle,
} from "lucide-react";
import { toast } from "../../lib/toast";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Input } from "../../components/ui/Input";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import { Highlight } from "../../components/ui/Highlight";
import { Modal } from "../../components/ui/Modal";
import { Pagination } from "../../components/ui/Pagination";
import { DatePicker } from "../../components/ui/DatePicker";
import { Select } from "../../components/ui/Select";
import { StatusPill } from "../../components/ui/StatusPill";
import { Table, type TableColumn } from "../../components/ui/Table";
import { Toolbar, ToolbarGrow } from "../../components/ui/Toolbar";
import { LiveIndicator } from "../../components/ui/LiveIndicator";
import { MESSAGE_POLL_MS, polling } from "../../lib/polling";
import { ConversationThread } from "../conversations/ConversationThread";
import { api, apiErrorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import {
  CANCELLABLE,
  FAILURE_EXPLANATION,
  RETRYABLE,
  formatWhen,
  type CursorPageOf,
  type MessageListItem,
  type MessageStatusValue,
} from "./types";
import { useMinimumDuration } from "../../lib/useMinimumDuration";

const STATUSES: MessageStatusValue[] = [
  "PENDING",
  "QUEUED",
  "SENDING",
  "SENT",
  "DELIVERED",
  "FAILED",
  "CANCELLED",
];

interface MessageHistoryProps {
  /** Fixed direction for the Sent / Received tabs; omitted on an "all" view. */
  direction?: "SENT" | "RECEIVED";
  /** Scheduled tab: only messages with a future send time. */
  scheduledOnly?: boolean;
  emptyTitle: string;
  emptyDescription: string;
}

function toCsv(rows: MessageListItem[]): string {
  const header = [
    "id",
    "direction",
    "status",
    "recipient",
    "sender",
    "body",
    "segments",
    "created",
    "delivered",
  ];
  const escape = (value: unknown) =>
    `"${String(value ?? "").replaceAll('"', '""')}"`;
  const lines = rows.map((r) =>
    [
      r._id,
      r.direction,
      r.status,
      r.recipient,
      r.sender,
      r.body,
      r.segments,
      r.createdAt,
      r.deliveredAt,
    ]
      .map(escape)
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
}

/**
 * True when a message offers no action, so the dialog can omit the footer entirely rather than
 * render an empty bordered strip under a delivered message.
 */
function readOnlyActions(m: { status: MessageStatusValue }): boolean {
  return !RETRYABLE.includes(m.status) && !CANCELLABLE.includes(m.status);
}

export function MessageHistory({
  direction,
  scheduledOnly,
  emptyTitle,
  emptyDescription,
}: MessageHistoryProps) {
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Cursor pagination is forward-only, so the stack is what makes a Back button possible.
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  // Set only while the user has jumped to the final page. Every other navigation clears it,
  // because "last" is absolute rather than a position you can page away from.
  const [atLast, setAtLast] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<MessageListItem | null>(null);

  const cursor = cursorStack.at(-1) ?? undefined;

  /** Which tab this list is, for the detail view's breadcrumb. */
  const tabLabel = scheduledOnly
    ? "Scheduled"
    : direction === "RECEIVED"
    ? "Received"
    : direction === "SENT"
    ? "Sent"
    : "All messages";

  // Cursors were issued against the previous page size; reusing them would skip or repeat rows.
  useEffect(() => {
    setCursorStack([]);
  }, [pageSize]);
  const params: Record<string, unknown> = { limit: pageSize };
  if (direction) params.direction = direction;
  if (status) params.status = status;
  if (search.trim()) params.search = search.trim();
  if (from) params.from = new Date(from).toISOString();
  if (to) params.to = new Date(to).toISOString();
  if (cursor && !atLast) params.cursor = cursor;
  if (atLast) params.last = true;

  const listQuery = useQuery({
    queryKey: ["messages", params],
    queryFn: async () =>
      (
        await api.get<CursorPageOf<MessageListItem>>("/gateway/messages", {
          params,
        })
      ).data,
    // Polls the CURRENT key. `params` carries the search text, status, date range, page size
    // and cursor, so each refetch reproduces exactly the view the user is looking at — it
    // cannot silently drop a filter or bounce them back to page one.
    ...polling(MESSAGE_POLL_MS),
  });

  const listQueryLoading = useMinimumDuration(listQuery.isLoading);

  const retryMutation = useMutation({
    mutationFn: async (id: string) => api.post(`/gateway/messages/${id}/retry`),
    onSuccess: () => {
      toast.success("Message requeued.");
      setSelected(null);
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not retry.")),
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/gateway/messages/${id}`),
    onSuccess: () => {
      toast.success("Message cancelled.");
      setSelected(null);
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not cancel.")),
  });

  // The slide-over renders the row captured when it was opened. Re-reading that row from the
  // freshly polled page keeps an open detail view honest — a message moving SENT -> DELIVERED
  // while the user reads it updates in place — without touching whether the panel is open,
  // which stays purely user-driven through setSelected.
  //
  // The fallback to the captured snapshot is the important half: if a poll drops the row from
  // the current page (its status stopped matching the filter, or it slid onto another page)
  // the panel must keep showing what the user opened rather than blanking or closing itself.
  // Which conversation the detail dialog has been switched into, or null while it is showing a
  // single message. Kept separate from `selected` so closing the thread returns to the message
  // the user opened rather than dumping them back to the table.
  const [threadPeer, setThreadPeer] = useState<string | null>(null);

  const selectedLive = selected
    ? listQuery.data?.items.find((row) => row._id === selected._id) ?? selected
    : null;

  // Scheduled has no server-side flag of its own; it's "has a future send time", which the
  // date filter can't express — so it is applied to the page after fetching.
  const rows = (listQuery.data?.items ?? []).filter((row) =>
    scheduledOnly
      ? row.scheduledAt && new Date(row.scheduledAt).getTime() > Date.now()
      : true
  );

  function exportCsv() {
    const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `smsfort-messages-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const columns: TableColumn<MessageListItem>[] = [
    {
      key: "direction",
      header: "",
      width: "44px",
      render: (row) =>
        row.direction === "SENT" ? (
          <Send className="size-4 text-text-muted" aria-label="Sent" />
        ) : (
          <Inbox className="size-4 text-text-muted" aria-label="Received" />
        ),
    },
    {
      key: "peer",
      header: "Contact",
      width: "170px",
      render: (row) => (
        <Highlight
          text={row.recipient ?? row.sender ?? "—"}
          query={search}
          className="font-medium text-text"
        />
      ),
    },
    {
      key: "body",
      header: "Message",
      render: (row) => (
        <Highlight
          text={row.body}
          query={search}
          className="line-clamp-1 text-text-muted"
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "120px",
      render: (row) => <StatusPill status={row.status} />,
    },
    {
      key: "createdAt",
      header: "When",
      width: "150px",
      align: "right",
      render: (row) => (
        <span className="text-text-muted">{formatWhen(row.createdAt)}</span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <Toolbar sticky>
        <ToolbarGrow>
          <Input
            label="Search"
            placeholder="Number or message text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCursorStack([]);
            }}
          />
        </ToolbarGrow>

        <Select
          label="Status"
          id="filter-status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCursorStack([]);
          }}
          className="sm:w-44"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>

        {/*
          Native date inputs carry their own intrinsic height and inner padding, which is why
          they never lined up with the search field. Input's own classes are applied to them
          here so all four controls resolve to the same 40px box and the same focus ring; the
          picker itself stays native, so keyboard and mobile behaviour is unchanged.
        */}
        <DatePicker
          id="filter-from"
          label="From"
          placeholder="Any date"
          value={from}
          onChange={(v) => {
            setFrom(v);
            setCursorStack([]);
          }}
          className="sm:w-44"
        />

        <DatePicker
          id="filter-to"
          label="To"
          placeholder="Any date"
          value={to}
          onChange={(v) => {
            setTo(v);
            setCursorStack([]);
          }}
          className="sm:w-44"
        />

        <Button
          variant="secondary"
          leftIcon={<Download className="size-4" aria-hidden />}
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="sm:ml-auto"
        >
          Export CSV
        </Button>

        <LiveIndicator className="hidden self-center sm:inline-flex" />
      </Toolbar>

      {listQuery.isError && (
        <p
          role="alert"
          className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger"
        >
          {apiErrorMessage(listQuery.error, "Could not load messages.")}
        </p>
      )}

      <Table
        stickyFirstColumn
        stickyHeader
        columns={columns}
        data={rows}
        getRowId={(row) => row._id}
        loading={listQueryLoading}
        onRowPress={(row) => setSelected(row)}
        emptyState={
          <EmptyState
            icon={<Search className="size-6" aria-hidden />}
            title={emptyTitle}
            description={emptyDescription}
          />
        }
      />

      {/* cursor mode: /messages returns { items, nextCursor, hasMore } and no total, so the
          bar shows a range without an "of Z" and disables jump-to-last rather than guessing. */}
      <Pagination
        mode="cursor"
        total={listQuery.data?.total}
        page={
          atLast && listQuery.data?.total
            ? Math.max(1, Math.ceil(listQuery.data.total / pageSize))
            : cursorStack.length + 1
        }
        pageSize={pageSize}
        itemCount={rows.length}
        hasMore={Boolean(listQuery.data?.hasMore && listQuery.data.nextCursor)}
        onFirst={() => {
          setAtLast(false);
          setCursorStack([]);
        }}
        onLast={() => setAtLast(true)}
        onPrev={() => {
          setAtLast(false);
          setCursorStack((stack) => stack.slice(0, -1));
        }}
        onNext={() =>
          setCursorStack((stack) =>
            listQuery.data?.nextCursor
              ? [...stack, listQuery.data.nextCursor]
              : stack
          )
        }
        onPageSizeChange={setPageSize}
      />

      <Modal
        open={selectedLive !== null}
        onClose={() => {
          setThreadPeer(null);
          setSelected(null);
        }}
        title={
          threadPeer ? `Conversation with ${threadPeer}` : "Message detail"
        }
        // The thread needs room to read as a conversation rather than a cramped popup; the single
        // message detail does not, so the dialog grows only when it switches modes.
        size={threadPeer ? "lg" : "md"}
        // Actions live in the pinned footer rather than at the end of the scrolling body, so
        // Retry and Cancel stay reachable on a long message instead of sitting below the fold.
        // Suppressed in conversation mode, where the compose box is the action.
        footer={
          selectedLive && !threadPeer && !readOnlyActions(selectedLive) ? (
            <div className="flex justify-end gap-2">
              {RETRYABLE.includes(selectedLive.status) && (
                <Button
                  variant="secondary"
                  leftIcon={<RotateCcw className="size-4" aria-hidden />}
                  loading={retryMutation.isPending}
                  onClick={() => retryMutation.mutate(selectedLive._id)}
                >
                  Retry
                </Button>
              )}
              {CANCELLABLE.includes(selectedLive.status) && (
                <Button
                  variant="danger"
                  leftIcon={<XCircle className="size-4" aria-hidden />}
                  loading={cancelMutation.isPending}
                  onClick={() => cancelMutation.mutate(selectedLive._id)}
                >
                  Cancel
                </Button>
              )}
            </div>
          ) : undefined
        }
      >
        {selectedLive && threadPeer && (
          <div className="flex min-h-0 flex-col gap-4">
            <Breadcrumb
              items={[
                {
                  label: "Messaging",
                  onClick: () => {
                    setThreadPeer(null);
                    setSelected(null);
                  },
                },
                {
                  label: tabLabel,
                  onClick: () => {
                    setThreadPeer(null);
                    setSelected(null);
                  },
                },
                { label: "Message detail", onClick: () => setThreadPeer(null) },
                { label: "Conversation" },
              ]}
            />
            {/*
              The same component the dedicated Conversations page will mount. Two entry points,
              one implementation — so they cannot drift into showing different things.
            */}
            <ConversationThread peer={threadPeer} />
          </div>
        )}

        {selectedLive && !threadPeer && (
          <div className="flex flex-col gap-5">
            <Breadcrumb
              items={[
                { label: "Messaging", onClick: () => setSelected(null) },
                { label: tabLabel, onClick: () => setSelected(null) },
                { label: "Message detail" },
              ]}
            />

            {/*
              Identity block. The counterparty is the headline — it is what the user came here to
              read — with the status beside it and the direction as a quiet label. Previously the
              number trailed off the end of a muted sentence and carried no more weight than the
              word "to".
            */}
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-xs uppercase tracking-wide text-text-muted">
                  {selectedLive.direction === "SENT" ? "To" : "From"}
                </span>
                <StatusPill status={selectedLive.status} />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* tnum so a column of digits does not shift as the dialog re-renders. */}
                <span className="tnum break-all text-lg font-semibold text-text">
                  {selectedLive.recipient ?? selectedLive.sender ?? "—"}
                </span>
                {(() => {
                  // Same derivation the backend uses for `peer`: recipient outbound, sender
                  // inbound. A row with neither has no conversation to open, so no button.
                  const peer =
                    selectedLive.direction === "SENT"
                      ? selectedLive.recipient
                      : selectedLive.sender;
                  if (!peer) return null;
                  return (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={
                        <MessagesSquare className="size-4" aria-hidden />
                      }
                      onClick={() => setThreadPeer(peer)}
                    >
                      View full conversation
                    </Button>
                  );
                })()}
              </div>
            </div>

            {/*
              A quote rule rather than a bordered box: the box read as a disabled text input, so
              the message itself looked like a form field the user had been locked out of.
            */}
            <section className="flex flex-col gap-1.5">
              <h3 className="text-xs uppercase tracking-wide text-text-muted">
                Message
              </h3>
              <blockquote className="whitespace-pre-wrap break-words border-l-2 border-accent bg-surface-hover/40 py-2.5 pl-3 pr-2 text-sm leading-relaxed text-text">
                {selectedLive.body}
              </blockquote>
              <p className="text-xs text-text-muted">
                {selectedLive.segments} segment
                {selectedLive.segments === 1 ? "" : "s"}
              </p>
            </section>

            {selectedLive.failureCode && (
              <section className="rounded-control border border-danger/30 bg-danger-soft px-3 py-2.5">
                <p className="text-sm font-medium text-danger">
                  {selectedLive.failureCode}
                </p>
                <p className="mt-1 text-sm text-danger/90">
                  {selectedLive.failureReason ??
                    FAILURE_EXPLANATION[selectedLive.failureCode] ??
                    "No further detail was reported."}
                </p>
              </section>
            )}

            {/*
              A stepper rather than a two-column list.

              The old grid printed all six lifecycle stamps whether or not they had happened, so a
              typical message showed one date and five em dashes and the eye had to hunt for the
              single row carrying information. Stages the message actually reached are solid and
              dated; the rest are hollow and dimmed, which turns the same data into a progress
              reading at a glance.
            */}
            <section className="flex flex-col gap-2">
              <h3 className="text-xs uppercase tracking-wide text-text-muted">
                Timeline
              </h3>
              <ol className="flex flex-col">
                {(
                  [
                    ["Created", selectedLive.createdAt],
                    ["Scheduled", selectedLive.scheduledAt],
                    ["Queued", selectedLive.queuedAt],
                    ["Picked up", selectedLive.sendingAt],
                    ["Sent", selectedLive.sentAt],
                    ["Delivered", selectedLive.deliveredAt],
                  ] as [string, string | null][]
                ).map(([label, value], i, all) => {
                  const reached = Boolean(value);
                  const isLast = i === all.length - 1;
                  return (
                    <li key={label} className="flex gap-3">
                      {/* Dot plus connector, drawn by the row itself so the rail scales with the
                          row height instead of needing a fixed offset. */}
                      <div className="flex flex-col items-center">
                        <span
                          aria-hidden
                          className={cn(
                            "mt-1.5 size-2 shrink-0 rounded-full",
                            reached
                              ? "bg-accent"
                              : "border border-border bg-transparent"
                          )}
                        />
                        {!isLast && (
                          <span aria-hidden className="w-px flex-1 bg-border" />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 pb-3.5">
                        <span
                          className={cn(
                            "text-sm",
                            reached ? "text-text" : "text-text-muted/70"
                          )}
                        >
                          {label}
                        </span>
                        <span
                          className={cn(
                            "tnum text-xs",
                            reached ? "text-text-muted" : "text-text-muted/40"
                          )}
                        >
                          {value ? formatWhen(value) : "—"}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          </div>
        )}
      </Modal>
    </div>
  );
}
